// Supabase Service - Subscriptions, onboarding events, user profiles
// Integrates with Supabase tables from SUPABASE_INTEGRATION.sql

import { createClient } from '@supabase/supabase-js';
import { logger } from '../utils/logger';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
if (!supabaseUrl || !supabaseKey) {
  throw new Error('Supabase env vars not configured. Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY.');
}

const supabase = createClient(supabaseUrl, supabaseKey);

export type OnboardingEventType = 'signup' | 'exam_date_set' | 'first_practice' | 'first_ai_exam' | 'subscription';

/** Save subscription to Supabase (called after Stripe payment success) */
export async function saveSubscription(
  userId: string,
  data: {
    stripeSubscriptionId: string;
    stripeCustomerId?: string;
    planType: 'weekly' | 'monthly' | 'lifetime';
    currentPeriodEnd?: string;
  }
): Promise<boolean> {
  try {
    const { error } = await supabase.from('subscriptions').insert({
      user_id: userId,
      stripe_subscription_id: data.stripeSubscriptionId,
      stripe_customer_id: data.stripeCustomerId || null,
      plan_type: data.planType,
      status: 'active',
      current_period_end: data.currentPeriodEnd || null
    });
    if (error) {
      logger.error('Supabase saveSubscription error:', error);
      return false;
    }
    return true;
  } catch (e) {
    logger.error('saveSubscription error:', e);
    return false;
  }
}

/** Check if user has active subscription in Supabase */
export async function getSubscription(userId: string): Promise<{ planType: string; status: string } | null> {
  try {
    const { data, error } = await supabase
      .from('subscriptions')
      .select('plan_type, status, current_period_end')
      .eq('user_id', userId)
      .eq('status', 'active')
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) {
      logger.error('Supabase getSubscription error:', error);
      return null;
    }
    if (!data) return null;

    // Check if period has ended
    if (data.current_period_end) {
      const end = new Date(data.current_period_end);
      if (end < new Date()) return null; // Expired
    }
    return { planType: data.plan_type, status: data.status };
  } catch (e) {
    logger.error('getSubscription error:', e);
    return null;
  }
}

/** Get subscription details (active or cancelled) for current plan display and cancel flow */
export async function getSubscriptionDetails(userId: string): Promise<{
  planType: string;
  status: string;
  currentPeriodEnd: string | null;
  stripeSubscriptionId: string | null;
} | null> {
  try {
    const { data, error } = await supabase
      .from('subscriptions')
      .select('plan_type, status, current_period_end, stripe_subscription_id')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error || !data) return null;
    return {
      planType: data.plan_type,
      status: data.status,
      currentPeriodEnd: data.current_period_end,
      stripeSubscriptionId: data.stripe_subscription_id
    };
  } catch (e) {
    logger.error('getSubscriptionDetails error:', e);
    return null;
  }
}

/** Update subscription status (e.g. when user cancels at period end) */
export async function updateSubscriptionStatus(
  userId: string,
  stripeSubscriptionId: string,
  status: string
): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('subscriptions')
      .update({ status, updated_at: new Date().toISOString() })
      .eq('user_id', userId)
      .eq('stripe_subscription_id', stripeSubscriptionId);

    if (error) {
      logger.error('Supabase updateSubscriptionStatus error:', error);
      return false;
    }
    return true;
  } catch (e) {
    logger.error('updateSubscriptionStatus error:', e);
    return false;
  }
}

/** Track onboarding funnel event */
export async function trackOnboardingEvent(
  userId: string,
  eventType: OnboardingEventType,
  eventData?: Record<string, unknown>
): Promise<boolean> {
  try {
    const { error } = await supabase.from('onboarding_events').insert({
      user_id: userId,
      event_type: eventType,
      event_data: eventData || null
    });
    if (error) {
      logger.error('Supabase trackOnboardingEvent error:', error);
      return false;
    }
    return true;
  } catch (e) {
    logger.error('trackOnboardingEvent error:', e);
    return false;
  }
}

/** Upsert user profile (first_name, exam_date, onboarding_completed_at) */
export async function upsertUserProfile(
  userId: string,
  data: { first_name?: string; exam_date?: string; onboarding_completed_at?: string; user_goal?: string }
): Promise<boolean> {
  try {
    const payload: Record<string, unknown> = { user_id: userId };
    if (data.first_name) payload.first_name = data.first_name;
    if (data.exam_date) payload.exam_date = data.exam_date;
    if (data.onboarding_completed_at) payload.onboarding_completed_at = data.onboarding_completed_at;
    if (data.user_goal) payload.user_goal = data.user_goal;

    const { error } = await supabase.from('user_profiles').upsert(payload, { onConflict: 'user_id' });
    if (error) {
      logger.error('Supabase upsertUserProfile error:', error);
      return false;
    }
    return true;
  } catch (e) {
    logger.error('upsertUserProfile error:', e);
    return false;
  }
}

/** Fetch full profile from user_profiles (first_name, last_name) for settings page */
export async function getProfile(userId: string): Promise<{ first_name: string | null; last_name: string | null } | null> {
  try {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('first_name, last_name')
      .eq('user_id', userId)
      .maybeSingle();

    if (error) {
      logger.error('Supabase getProfile error:', error);
      return null;
    }
    return data ? { first_name: data.first_name || null, last_name: data.last_name || null } : null;
  } catch (e) {
    logger.error('getProfile error:', e);
    return null;
  }
}

/** Upsert profile (first_name, last_name to Supabase) */
export async function upsertProfile(
  userId: string,
  data: { first_name?: string; last_name?: string }
): Promise<boolean> {
  try {
    const payload: Record<string, unknown> = { user_id: userId };
    if (data.first_name !== undefined) payload.first_name = data.first_name;
    if (data.last_name !== undefined) payload.last_name = data.last_name;

    const { error } = await supabase.from('user_profiles').upsert(payload, { onConflict: 'user_id' });
    if (error) {
      logger.error('Supabase upsertProfile error:', error);
      return false;
    }
    return true;
  } catch (e) {
    logger.error('upsertProfile error:', e);
    return false;
  }
}

/** Save last AI exam score (raw out of 50) to user_profiles. Single source of truth for dashboard + intro. */
export async function saveLastExamScore(userId: string, correctCount: number): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('user_profiles')
      .upsert(
        { user_id: userId, last_exam_score: correctCount, updated_at: new Date().toISOString() },
        { onConflict: 'user_id' }
      );
    if (error) {
      logger.error('Supabase saveLastExamScore error:', error);
      return false;
    }
    return true;
  } catch (e) {
    logger.error('saveLastExamScore error:', e);
    return false;
  }
}

/** Get last AI exam score (raw out of 50) from user_profiles. */
export async function getLastExamScore(userId: string): Promise<number | null> {
  try {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('last_exam_score')
      .eq('user_id', userId)
      .maybeSingle();
    if (error) {
      logger.error('Supabase getLastExamScore error:', error);
      return null;
    }
    const v = data?.last_exam_score;
    if (v == null || typeof v !== 'number') return null;
    return Math.round(v);
  } catch (e) {
    logger.error('getLastExamScore error:', e);
    return null;
  }
}

/** Get exam_date from user_profiles for ExamDateScreen skip logic */
export async function getExamDate(userId: string): Promise<string | null> {
  try {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('exam_date')
      .eq('user_id', userId)
      .maybeSingle();
    if (error) {
      logger.error('Supabase getExamDate error:', error);
      return null;
    }
    const v = data?.exam_date;
    return typeof v === 'string' ? v : null;
  } catch (e) {
    logger.error('getExamDate error:', e);
    return null;
  }
}

/** Fetch first_name from user_profiles for greeting personalization */
export async function getUserFirstName(userId: string): Promise<string | null> {
  try {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('first_name')
      .eq('user_id', userId)
      .maybeSingle();

    if (error) {
      logger.error('Supabase getUserFirstName error:', error);
      return null;
    }
    return data?.first_name || null;
  } catch (e) {
    logger.error('getUserFirstName error:', e);
    return null;
  }
}
